import React from "react";
import { Badge } from "reactstrap";
import { Button as UagButton, Icon } from "../../../components/Component";
import { formatWeighbridgeWeightMt, mapWeighbridgeWeightsToGrn } from "../../../utils/weighbridgeUtils";
import { resolveWeighbridgeTicketPrefillData } from "../../../utils/weighbridgeTicketNormalization";

const WEIGHBRIDGE_WEIGHT_UNIT = "MT";

const getDisplayValue = (value) => {
  if (value === null || value === undefined || value === "") return "--";
  return value;
};

const formatLabelValue = (value) => getDisplayValue(value);

const formatWeightValue = (value) => {
  return formatWeighbridgeWeightMt(value, { unit: WEIGHBRIDGE_WEIGHT_UNIT });
};

const formatDateValue = (value) => {
  if (!value) return "--";

  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    return "--";
  }
};

const formatReadableText = (value) => {
  const normalizedValue = getDisplayValue(value);

  if (normalizedValue === "--") return normalizedValue;

  return String(normalizedValue).replaceAll("_", " ");
};

const getTicketStatusTone = (value) => {
  const normalizedValue = String(value || "").toLowerCase();

  if (
    normalizedValue.includes("complete") ||
    normalizedValue.includes("delivered") ||
    normalizedValue.includes("received")
  ) {
    return "success";
  }

  if (
    normalizedValue.includes("processing") ||
    normalizedValue.includes("transit") ||
    normalizedValue.includes("offloading") ||
    normalizedValue.includes("onboarding")
  ) {
    return "warning";
  }

  return "secondary";
};

const getUsageTone = (usageStatus) =>
  String(usageStatus || "").toUpperCase() === "USED" ? "danger" : "success";

const getUsageLabel = (usageStatus) =>
  String(usageStatus || "").toUpperCase() === "USED" ? "Used" : "Available";

const SummaryItem = ({ label, value, strong = false }) => (
  <div className="col-sm-6 col-lg-3">
    <div className="h-100 border rounded-3 bg-white p-3">
      <div className="sub-text text-uppercase mb-1">{label}</div>
      <div className={strong ? "fw-bold text-dark" : "caption-text text-dark"}>
        {value}
      </div>
    </div>
  </div>
);

const DetailItem = ({ label, value }) => (
  <div className="col-md-6 col-xl-4">
    <div className="border rounded-3 bg-white p-3 h-100">
      <div className="sub-text text-uppercase mb-1">{label}</div>
      <div className="caption-text text-dark">{value}</div>
    </div>
  </div>
);

const SelectedWeighbridgeTicketCard = ({
  ticket,
  flowLabel = "workflow",
  weightDisplayMode = "standard",
  onChangeTicket,
}) => {
  if (!ticket) return null;

  const resolvedTicketPrefill = resolveWeighbridgeTicketPrefillData(ticket);
  const grnDisplayWeights = mapWeighbridgeWeightsToGrn(resolvedTicketPrefill);
  const displayFirstWeight =
    weightDisplayMode === "grn"
      ? grnDisplayWeights?.first_weight
      : ticket?.first_weight || resolvedTicketPrefill?.first_weight;
  const displaySecondWeight =
    weightDisplayMode === "grn"
      ? grnDisplayWeights?.second_weight
      : ticket?.second_weight || resolvedTicketPrefill?.second_weight;
  const displayNetWeight =
    weightDisplayMode === "grn"
      ? grnDisplayWeights?.net_weight
      : ticket?.net_weight || resolvedTicketPrefill?.net_weight;

  const ticketReference =
    ticket?.weigh_bridge_ticket_reference_no ||
    resolvedTicketPrefill?.weigh_bridge_ticket_reference_no ||
    ticket?.weigh_bridge_ticket_number ||
    resolvedTicketPrefill?.weigh_bridge_ticket_number ||
    "--";
  const usageLabel = getUsageLabel(ticket?.usage_status);
  const firstWeightLabel = weightDisplayMode === "grn" ? "Gross Weight" : "Tare Weight";
  const secondWeightLabel = weightDisplayMode === "grn" ? "Tare Weight" : "Gross Weight";

  return (
    <div className="mt-3 border rounded-3 bg-light overflow-hidden">
      <div className="p-3 p-md-4 border-bottom bg-white">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
          <div>
            <div className="d-flex align-items-center gap-2 mb-2">
              <span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-primary text-white" style={{ width: 36, height: 36 }}>
                <Icon name="truck" />
              </span>
              <div>
                <div className="fw-bold">Selected Weighbridge Ticket</div>
                <div className="text-soft small">
                  Ticket details linked to this {flowLabel}.
                </div>
              </div>
            </div>

            <div className="d-flex flex-wrap gap-2">
              <Badge color="primary" className="badge-dim">
                #{ticketReference}
              </Badge>
              <Badge color={getTicketStatusTone(ticket?.weigh_bridge_ticket_status)} className="badge-dim">
                {formatReadableText(ticket?.weigh_bridge_ticket_status)}
              </Badge>
              <Badge color="info" className="badge-dim">
                {formatReadableText(ticket?.designation)}
              </Badge>
              <Badge color={getUsageTone(ticket?.usage_status)} className="badge-dim">
                {usageLabel}
              </Badge>
            </div>
          </div>

          {typeof onChangeTicket === "function" && (
            <UagButton color="light" outline className="btn-white" onClick={onChangeTicket}>
              <Icon name="edit" />
              <span>Change Ticket</span>
            </UagButton>
          )}
        </div>
      </div>

      <div className="p-3 p-md-4">
        <div className="row g-3 mb-3">
          <SummaryItem label="Ticket Ref No." value={formatLabelValue(ticketReference)} strong />
          <SummaryItem label="Ticket No." value={formatLabelValue(ticket?.weigh_bridge_ticket_number)} />
          <SummaryItem
            label="External Ref No."
            value={formatLabelValue(
              ticket?.external_weigh_bridge_ticket_number ||
                resolvedTicketPrefill?.external_weigh_bridge_ticket_number,
            )}
          />
          <SummaryItem
            label="Created On"
            value={formatDateValue(
              ticket?.created_on ||
                resolvedTicketPrefill?.created_on ||
                ticket?.time_in ||
                resolvedTicketPrefill?.time_in,
            )}
          />
        </div>

        <div className="row g-3">
          <DetailItem label="Time In" value={formatDateValue(ticket?.time_in || resolvedTicketPrefill?.time_in)} />
          <DetailItem label={firstWeightLabel} value={formatWeightValue(displayFirstWeight)} />
          <DetailItem label={secondWeightLabel} value={formatWeightValue(displaySecondWeight)} />
          <DetailItem label="Net Weight" value={formatWeightValue(displayNetWeight)} />
          <DetailItem
            label="Truck Plate"
            value={formatLabelValue(ticket?.truck_licence_plate || resolvedTicketPrefill?.truck_licence_plate)}
          />
          <DetailItem
            label="Driver"
            value={formatLabelValue(ticket?.truck_driver_name || resolvedTicketPrefill?.truck_driver_name)}
          />
          <DetailItem
            label="Transporter"
            value={formatLabelValue(ticket?.transporter_name || resolvedTicketPrefill?.transporter_name)}
          />
          <DetailItem
            label="Trailer One"
            value={formatLabelValue(ticket?.trailer_one || resolvedTicketPrefill?.trailer_one)}
          />
          <DetailItem
            label="Trailer Two"
            value={formatLabelValue(ticket?.trailer_two || resolvedTicketPrefill?.trailer_two)}
          />
        </div>
      </div>
    </div>
  );
};

export default SelectedWeighbridgeTicketCard;
